import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Award, Shield, Dumbbell, Star, MapPin, Clock, Calendar as CalendarIcon,
  ArrowLeft, CheckCircle, DollarSign, Loader2, Users, Zap, CreditCard, Timer
} from "lucide-react";
import { format } from "date-fns";
import { KycBookingNotice } from "@/components/KycBookingNotice";
import { getBookingKycMessage, hasApprovedBookingKyc } from "@/lib/bookingKyc";

const ROLE_ICONS = { coach: Award, trainer: Dumbbell, official: Shield };
const ROLE_COLORS = { coach: "bg-blue-100 text-blue-700", trainer: "bg-red-100 text-red-700", official: "bg-purple-100 text-purple-700" };

export default function SPProfile() {
  const { spId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slotsLoading, setSlotsLoading] = useState(false);
  // Booking state
  const [bookOpen, setBookOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookRole, setBookRole] = useState("");
  const [bookDuration, setBookDuration] = useState("1");
  const [bookPricingModel, setBookPricingModel] = useState("per_hour");
  const [bookVenue, setBookVenue] = useState("tbc");
  const [bookSessionType, setBookSessionType] = useState("single");
  const [bookSeriesCount, setBookSeriesCount] = useState(1);
  const [bookNotes, setBookNotes] = useState("");
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api.get(`/sp/public/${spId}`).then(r => {
      setSp(r.data);
      if (r.data.roles_enabled?.length) setBookRole(r.data.roles_enabled[0]);
    }).catch(() => toast.error("Professional not found")).finally(() => setLoading(false));
  }, [spId]);

  useEffect(() => {
    if (!spId || !selectedDate) return;
    setSlotsLoading(true);
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    api.get(`/sp/available-slots/${spId}?date=${dateStr}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [spId, selectedDate]);

  const handleSlotClick = (slot) => {
    if (!slot.available) return;
    if (!user) { navigate("/login"); return; }
    if (!hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }
    setSelectedSlot(slot);
    setBookOpen(true);
  };

  const getPrice = () => {
    const pricing = sp?.pricing || {};
    const base = pricing[bookPricingModel] || pricing.per_hour || 500;
    let total = base;
    if (bookPricingModel === "per_hour") total = base * parseInt(bookDuration || 1);
    if (bookSessionType === "series" && bookSeriesCount > 1) {
      total = base * bookSeriesCount * (1 - (pricing.series_discount_pct || 10) / 100);
    }
    const commission = Math.round(total * 0.10);
    return { base, total: Math.round(total), commission, payable: Math.round(total) };
  };

  const handleBook = async () => {
    if (!selectedSlot || !user) return;
    if (!hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }
    setBooking(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const durH = parseInt(bookDuration || 1);
      const startH = parseInt(selectedSlot.start.split(":")[0]);
      const endTime = `${(startH + durH).toString().padStart(2, "0")}:00`;
      const res = await api.post("/sp/book", {
        sp_id: spId, role: bookRole, date: dateStr,
        start_time: selectedSlot.start, end_time: endTime,
        pricing_model: bookPricingModel,
        venue_id: bookVenue === "tbc" ? null : bookVenue,
        notes: bookNotes, series_count: bookSessionType === "series" ? bookSeriesCount : 1,
      });
      toast.success(`Booked! Ref: ${res.data.booking_id}. ₱${res.data.amount} confirmed.`);
      setBookOpen(false);
      setSelectedSlot(null);
      // Refresh slots
      const ds = format(selectedDate, "yyyy-MM-dd");
      api.get(`/sp/available-slots/${spId}?date=${ds}`).then(r => setSlots(r.data.slots || []));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    }
    setBooking(false);
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!sp) return <div className="text-center py-20"><p className="font-heading text-xl">Professional not found</p></div>;

  const pricing = sp.pricing || {};
  const price = getPrice();
  const kycBlocked = user && !hasApprovedBookingKyc(user);
  const kycMessage = getBookingKycMessage(user);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6" data-testid="sp-profile-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" />Back</button>

      {/* Profile Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              <Award className="w-10 h-10 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="sp-name">{sp.name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {sp.verification_status === "verified" && <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-0.5" />Verified</Badge>}
                {sp.roles_enabled?.map(r => { const I = ROLE_ICONS[r] || Award; return <Badge key={r} className={ROLE_COLORS[r]}><I className="w-3 h-3 mr-0.5" />{r}</Badge>; })}
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-500" /><strong>{sp.rating}</strong> ({sp.total_ratings} reviews)</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{sp.years_experience} years</span>
                <span className="flex items-center gap-1"><CalendarIcon className="w-4 h-4" />{sp.total_sessions} sessions</span>
              </div>
              {sp.service_locations?.length > 0 && (
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{sp.service_locations.join(", ")}</p>
              )}
            </div>
          </div>
          {sp.bio && <p className="text-muted-foreground mt-4">{sp.bio}</p>}

          {/* Certifications */}
          {sp.certifications?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Credentials</p>
              <div className="flex gap-2 flex-wrap">
                {sp.certifications.map(c => (
                  <Badge key={c.cert_id} variant="outline" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1 text-green-500" />{c.name} — {c.issuing_body}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pricing Card */}
        <Card className="border-primary/20">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base flex items-center gap-2"><DollarSign className="w-4 h-4" />Pricing</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {[["per_hour","Per Hour"],["per_game","Per Game"],["per_session","Per Session"],["half_day","Half Day"],["full_day","Full Day"]].map(([k,l]) =>
              pricing[k] ? <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span className="font-bold">₱{pricing[k].toLocaleString()}</span></div> : null
            )}
            {pricing.travel_surcharge > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Travel Surcharge</span><span>+₱{pricing.travel_surcharge}</span></div>}
            {pricing.series_discount_pct > 0 && <div className="flex justify-between text-sm text-green-600"><span>Series Discount</span><span>-{pricing.series_discount_pct}%</span></div>}
            <Separator />
            <p className="text-[10px] text-muted-foreground">10% platform commission applied. No hidden fees.</p>
          </CardContent>
        </Card>
      </div>

      {/* Availability + Slots */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div>
          <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Select Date</p>
          <Calendar mode="single" selected={selectedDate} onSelect={d => { if (d) setSelectedDate(d); }}
            disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
            className="border rounded-lg p-3" data-testid="sp-calendar" />
        </div>
        <div className="lg:col-span-2">
          <p className="font-heading text-base mb-3">Available Slots — {format(selectedDate, "MMM d, yyyy")}</p>
          {kycBlocked && <div className="mb-3"><KycBookingNotice message={kycMessage} compact testId="sp-profile-kyc-notice" /></div>}
          {slotsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : slots.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No availability set for this date</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="sp-slots">
              {slots.map((s, i) => (
                <button key={i} onClick={() => handleSlotClick(s)} disabled={!s.available || kycBlocked}
                  data-testid={`sp-slot-${i}`}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    selectedSlot?.start === s.start ? "border-primary bg-primary/5 ring-1 ring-primary" :
                    s.available ? "border-slate-200 hover:border-primary/50 hover:-translate-y-0.5 cursor-pointer" :
                    "border-slate-100 bg-slate-50 opacity-40 cursor-not-allowed"
                  }`}>
                  <p className="font-heading text-sm font-bold">{s.start}</p>
                  <p className="text-[10px] text-muted-foreground">{s.end}</p>
                  {!s.available && <p className="text-[8px] text-red-400 mt-0.5">Booked</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reviews */}
      {sp.recent_ratings?.length > 0 && (
        <Card className="mb-8">
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" />Reviews ({sp.total_ratings})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sp.recent_ratings.map(r => (
                <div key={r.rating_id} className="border-b last:border-0 pb-3 last:pb-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{r.client_name}</span>
                      <Badge variant="outline" className="text-[9px]">{r.role_rated}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      {Array.from({length: 5}).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? "text-amber-500 fill-amber-500" : "text-slate-200"}`} />)}
                    </div>
                  </div>
                  {r.review && <p className="text-sm text-muted-foreground">{r.review}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Booking Dialog ── */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sp-book-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Book {sp.name}</DialogTitle>
            <DialogDescription>Complete your booking in a few simple steps</DialogDescription>
          </DialogHeader>
          {selectedSlot && (
            <div className="space-y-4">
              {/* Step 1: Session Details */}
              <div className="space-y-3">
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-medium">{format(selectedDate, "MMM d, yyyy")}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Start Time</span><span className="font-medium">{selectedSlot.start}</span></div>
                </div>

                <div><Label className="text-xs">Role</Label>
                  <Select value={bookRole} onValueChange={setBookRole}>
                    <SelectTrigger data-testid="book-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{(sp.roles_enabled || []).map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                  </Select></div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Duration (hours)</Label>
                    <Select value={bookDuration} onValueChange={setBookDuration}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["1","2","3","4"].map(h => <SelectItem key={h} value={h}>{h} hour{h!=="1"?"s":""}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Pricing Model</Label>
                    <Select value={bookPricingModel} onValueChange={setBookPricingModel}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {pricing.per_hour && <SelectItem value="per_hour">Per Hour (₱{pricing.per_hour})</SelectItem>}
                        {pricing.per_game && <SelectItem value="per_game">Per Game (₱{pricing.per_game})</SelectItem>}
                        {pricing.per_session && <SelectItem value="per_session">Per Session (₱{pricing.per_session})</SelectItem>}
                        {pricing.half_day && <SelectItem value="half_day">Half Day (₱{pricing.half_day})</SelectItem>}
                        {pricing.full_day && <SelectItem value="full_day">Full Day (₱{pricing.full_day})</SelectItem>}
                      </SelectContent>
                    </Select></div>
                </div>

                <div><Label className="text-xs">Session Type</Label>
                  <Select value={bookSessionType} onValueChange={setBookSessionType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Session</SelectItem>
                      <SelectItem value="series">Series ({pricing.series_discount_pct || 10}% off)</SelectItem>
                    </SelectContent>
                  </Select></div>

                {bookSessionType === "series" && (
                  <div><Label className="text-xs">Number of Sessions</Label>
                    <Input type="number" min={2} max={20} value={bookSeriesCount} onChange={e => setBookSeriesCount(parseInt(e.target.value)||2)} /></div>
                )}

                <div><Label className="text-xs">Venue</Label>
                  <Select value={bookVenue} onValueChange={setBookVenue}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tbc">Venue To Be Confirmed</SelectItem>
                    </SelectContent>
                  </Select></div>

                <div><Label className="text-xs">Notes (optional)</Label>
                  <Textarea value={bookNotes} onChange={e => setBookNotes(e.target.value)} placeholder="Any specific requirements..." className="text-sm" /></div>
              </div>

              <Separator />

              {/* Step 2: Fee Breakdown */}
              <div className="space-y-1 text-sm">
                <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Fee Breakdown</p>
                <div className="flex justify-between"><span className="text-muted-foreground">Professional Fee ({bookPricingModel.replace("_"," ")})</span><span>₱{price.base.toLocaleString()}{bookPricingModel === "per_hour" ? ` × ${bookDuration}hr` : ""}</span></div>
                {bookSessionType === "series" && <div className="flex justify-between text-green-600"><span>Series Discount ({pricing.series_discount_pct || 10}% × {bookSeriesCount} sessions)</span><span>Applied</span></div>}
                <Separator />
                <div className="flex justify-between font-bold text-base"><span>Total Payable</span><span className="text-primary">₱{price.payable.toLocaleString()}</span></div>
                <p className="text-[10px] text-muted-foreground">Platform commission (10%) is deducted from the professional's fee. No extra charges to you.</p>
              </div>

              {/* Step 3: Confirm & Pay */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-sm text-amber-800">
                <Timer className="w-4 h-4 shrink-0" />
                <span>Payment is held in escrow and released after session completion.</span>
              </div>

              <Button onClick={handleBook} disabled={booking} className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-full font-heading uppercase tracking-wide" data-testid="confirm-sp-booking">
                {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm & Pay ₱${price.payable.toLocaleString()}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
